import apiService from './api';
import { MessageDetail, PaginatedMessagesDto, SendMessageDto } from '../types';

const MESSAGE_DETAIL_CACHE_TTL_MS = 10 * 60_000;
const SYSTEM_MESSAGE_DETAIL_CACHE_TTL_MS = 30 * 1000;
const MESSAGE_DETAIL_CACHE_VERSION = 'v1';

type CachedMessageDetail = {
  version: string;
  storedAt: number;
  username: string;
  mailId: string;
  detail: MessageDetail;
};

const getCurrentUsername = (): string => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return 'anonymous';
    const parsed = JSON.parse(rawUser) as { username?: string };
    return parsed?.username || 'anonymous';
  } catch {
    return 'anonymous';
  }
};

const getDetailCacheKey = (username: string, mailId: string) =>
  `mailbox:message-detail:${MESSAGE_DETAIL_CACHE_VERSION}:${username}:${mailId}`;

class MessageService {
  async getInbox(page: number = 1, pageSize: number = 20): Promise<PaginatedMessagesDto> {
    return await apiService.get<PaginatedMessagesDto>(`/api/Messages/inbox?page=${page}&pageSize=${pageSize}`);
  }

  async getSent(page: number = 1, pageSize: number = 20): Promise<PaginatedMessagesDto> {
    return await apiService.get<PaginatedMessagesDto>(`/api/Messages/sent?page=${page}&pageSize=${pageSize}`);
  }

  async getCredits(page: number = 1, pageSize: number = 20): Promise<PaginatedMessagesDto> {
    try {
      return await apiService.get<PaginatedMessagesDto>(`/api/Messages/credits-local?page=${page}&pageSize=${pageSize}`);
    } catch {
      return await apiService.get<PaginatedMessagesDto>(`/api/Messages/credits?page=${page}&pageSize=${pageSize}`);
    }
  }

  async getDebits(page: number = 1, pageSize: number = 20): Promise<PaginatedMessagesDto> {
    try {
      const localDebits = await apiService.get<PaginatedMessagesDto>(`/api/Messages/debits-local?page=${page}&pageSize=${pageSize}`);
      if (localDebits.messages && localDebits.messages.length > 0) {
        return localDebits;
      }

      // Premium username and card-funded credential purchases can be represented
      // as receipt system messages even when local ledger delta is zero.
      return await apiService.get<PaginatedMessagesDto>(`/api/Messages/debits?page=${page}&pageSize=${pageSize}`);
    } catch {
      return await apiService.get<PaginatedMessagesDto>(`/api/Messages/debits?page=${page}&pageSize=${pageSize}`);
    }
  }

  async getMessage(mailId: string): Promise<MessageDetail> {
    const username = getCurrentUsername();
    const cacheKey = getDetailCacheKey(username, mailId);
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw) as CachedMessageDetail;
        const isSystemReceipt = !!parsed?.detail?.isSystemMessage;
        const ttl = isSystemReceipt ? SYSTEM_MESSAGE_DETAIL_CACHE_TTL_MS : MESSAGE_DETAIL_CACHE_TTL_MS;
        if (
          parsed?.version === MESSAGE_DETAIL_CACHE_VERSION &&
          parsed.mailId === mailId &&
          Date.now() - parsed.storedAt <= ttl
        ) {
          // Serve cached message immediately while still allowing fresh fetch below if caller retries.
          return parsed.detail;
        }
      }
    } catch {
      // Ignore cache read errors.
    }

    const detail = await apiService.get<MessageDetail>(`/api/Messages/${mailId}`);
    try {
      const record: CachedMessageDetail = {
        version: MESSAGE_DETAIL_CACHE_VERSION,
        storedAt: Date.now(),
        username,
        mailId,
        detail,
      };
      localStorage.setItem(cacheKey, JSON.stringify(record));
    } catch {
      // Ignore cache write errors.
    }
    return detail;
  }

  async sendMessage(message: SendMessageDto): Promise<{ mailId: string; message: string }> {
    return await apiService.post<{ mailId: string; message: string }>('/api/Messages', message);
  }

  async markAsRead(mailId: string): Promise<void> {
    await apiService.put(`/api/Messages/${mailId}/read`, {});
  }

  async deleteMessage(mailId: string): Promise<void> {
    await apiService.delete(`/api/Messages/${mailId}`);
  }

  async cancelMessage(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/cancel`, {});
  }

  async refundMessage(mailId: string, reason?: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/refund`, { reason });
  }

  async expireMessage(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/expire`, {});
  }

  async archiveMessage(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/archive`, {});
  }

  async trashMessage(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/trash`, {});
  }

  async getArchive(page: number = 1, pageSize: number = 20): Promise<PaginatedMessagesDto> {
    return await apiService.get<PaginatedMessagesDto>(`/api/Messages/archive?page=${page}&pageSize=${pageSize}`);
  }

  async getTrash(page: number = 1, pageSize: number = 20): Promise<PaginatedMessagesDto> {
    return await apiService.get<PaginatedMessagesDto>(`/api/Messages/trash?page=${page}&pageSize=${pageSize}`);
  }

  async emptyTrash(): Promise<{ success: boolean; message: string; deletedCount: number }> {
    return await apiService.delete<{ success: boolean; message: string; deletedCount: number }>(`/api/Messages/trash/empty`);
  }

  async restoreFromTrash(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/restore`, {});
  }

  async restoreFromArchive(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/restore-from-archive`, {});
  }

  async rejectMessage(mailId: string): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(`/api/Messages/${mailId}/reject`, {});
  }

  async releaseAllReserves(): Promise<{ success: boolean; message: string; expiredCount: number; releasedAmount: number }> {
    return await apiService.post<{ success: boolean; message: string; expiredCount: number; releasedAmount: number }>('/api/Messages/release-reserves', {});
  }
}

export const messageService = new MessageService();
export default messageService;

